'use client'

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import Link from 'next/link';

// lib files
import { DONATE_FALLBACK_URL } from '@/lib/constants';
import { getPassportLearnMoreUrl } from '@/lib/helpers/get-passport-learn-more-url';
import { sendPbsGtmEvent } from '@/lib/helpers/send-pbs-gtm-event';
import { StationData } from '@/lib/types/api/stations-data';
import { useHasMounted } from '@/lib/hooks';
import { useIdentity } from '@/lib/hooks/useIdentity';

// components
import DonateLinkButton from '@/components/Button/DonateLinkButton';
import LinkButton from '@/components/Button/LinkButton';

// svg's
import HeartIcon from '@/public/svg/donate-heart.svg';

// styles
import styles from './PassportRowHeader.module.scss';

interface PassportRowHeaderProps {
  stationData: StationData;
  depIsPassportMember?: boolean;
  depIsLocalizedPassportMember?: boolean;
  displaySignedInDonateCTA?: boolean;
  displaySignedInExploreCTA?: boolean;
  displaySignedOutDonateCTA?: boolean;
  displaySignedOutExploreCTA?: boolean;
  displaySignedOutLearnMoreCTA?: boolean;
  displaySignedInCopy?: boolean;
}

const ExploreCTA = () => {
  return (
    <LinkButton
      href="/explore/passport/"
      style="light_blue"
      className={styles.passport_row_explore_button}
    >
      Explore the Best of Passport
    </LinkButton>
  );
};

const PassportRowHeader = (props: PassportRowHeaderProps) => {
  const hasMounted = useHasMounted();

  const { cachedIdentity } = useIdentity();
  // Checks if component is mounted on the client before attempting to
  // hydrate, otherwise a hydration error occurs
  if (!hasMounted) {
    return null;
  }

  const {
    depIsPassportMember = false,
    depIsLocalizedPassportMember = false,
    displaySignedInDonateCTA = true,
    displaySignedInExploreCTA = true,
    displaySignedOutDonateCTA = true,
    displaySignedOutExploreCTA = false,
    displaySignedOutLearnMoreCTA = true,
    displaySignedInCopy = true,
    stationData,
  } = props;

  const { attributes: station_info } = stationData;

  const {
    short_common_name,
    passport_url,
  } = station_info;

  const isPassportMember =
    // did we pass the depIsPassportMember prop (e.g. from storybook or tests)
    depIsPassportMember ||
    // are they is_passport
    cachedIdentity?.isPassport;

  // default to depIsLocalizedPassportMember prop for Storybook and tests
  // otherwise set to false
  let isLocalizedPassportMember = depIsLocalizedPassportMember || false;

  // if we get a profile, set isLocalizedPassportMember based on whether
  // they are a passport member of the localized station.
  if (cachedIdentity && !depIsPassportMember) {
    isLocalizedPassportMember = cachedIdentity.isPassportMemberOf(stationData);
  }

  const passportLearnMoreUrl = getPassportLearnMoreUrl(stationData);

  const donateUrl = passport_url ? passport_url : DONATE_FALLBACK_URL;

  if (isPassportMember) {
    return (
      <div className={styles.passport_row_header}>
        <h2 className={styles.passport_row_title}>
          Available with your {short_common_name} Passport benefit
        </h2>
        <p className={styles.passport_row_description}>
          { displaySignedInCopy && "Thank you for being a member! You are supporting America’s largest classroom, the nation’s largest stage for the arts and a trusted window into the world."}
          { displaySignedInDonateCTA && isLocalizedPassportMember &&
            <Link
              href={donateUrl}
              target="_blank"
              className={styles.passport_row_make_another_donation_link}
              onClick={() => {
                sendPbsGtmEvent({
                  feature_category: "passport row",
                  feature_name: "passport donate link (localized passport member)",
                  object_text: "Make Another Donation",
                  object_url: donateUrl,
                })
              }}
            >
              <HeartIcon/> Make Another Donation
            </Link>}
        </p>
        <div className={styles.passport_row_ctas}>
          { displaySignedInExploreCTA && <ExploreCTA /> }
          { displaySignedInDonateCTA && isLocalizedPassportMember &&
            <DonateLinkButton
              style="white_ghost"
              href={donateUrl}
              target="_blank"
              iconBefore='heart'
              className={styles.passport_row_make_another_donation_link_button}
              gtmEventData={{
                feature_category: "passport row",
                feature_name: "passport donate link (localized passport member)",
                object_text: "Make Another Donation",
                object_url: donateUrl,
              }}
            >
              Make Another Donation
            </DonateLinkButton>}
        </div>
      </div>
    )} else {
      return (
        <div className={styles.passport_row_header}>
          <h2 className={styles.passport_row_title}>
            Watch with {short_common_name} Passport
          </h2>
          <p className={styles.passport_row_description}>
            { "Support your local station and get extended access to your favorite PBS shows & films.   " }
            { displaySignedOutLearnMoreCTA && passportLearnMoreUrl && (
              <Link
                href={passportLearnMoreUrl}
                className={styles.passport_row_learn_more_link}
              >
                What is Passport?
              </Link>
            )}
          </p>
          <div className={styles.passport_row_ctas}>
            { displaySignedOutDonateCTA && (
              <DonateLinkButton
                href={donateUrl}
                style={'light_blue'}
                className={styles.passport_row_donate_button}
                gtmEventData={{
                  feature_category: "passport row",
                  feature_name: "passport row donate link",
                  object_text: "Donate & Start Watching",
                  object_url: donateUrl,
                }}
              >
                Donate & Start Watching
              </DonateLinkButton>
            )}
            { displaySignedOutExploreCTA && <ExploreCTA /> }
            { displaySignedOutLearnMoreCTA && passportLearnMoreUrl && (
              <LinkButton
                href={passportLearnMoreUrl}
                className={styles.passport_row_learn_more_link_button}
              >
                What is Passport?
              </LinkButton>
            )}
          </div>
        </div>
      )
  };
};

export default PassportRowHeader;
